import { IAlert } from 'src/app/core/models/alert.models';
import { ERROR_CODE, ERROR_STATUS } from 'src/app/shared/models/error.models';
import {
	IAudios,
	IMessages,
	IOperation,
	TConnectionLogs,
	TDocsLogs,
	TestStatus,
	THomeLogs,
	TOcrLogs,
	TPolLogs,
	TProcessLogs,
} from './user-identification';

/** Intervalo de captura del historial de audio POL (segundos). NUNCA MAYOR A 1 SEG. */
export const POL_SEG_HISTORY_INTERVAL: number = 0.5;

/** Span vertical frente–barbilla / alto óvalo exterior por debajo de este ratio → demasiado lejos (audio POL). */
export const POL_FACE_TOO_FAR_MIN_HEIGHT_RATIO: number = 0.35;

export interface IPolHistory {
	timestamp: string;
	faceDetection: boolean;
	isTooFar: boolean;
	isTooClose: boolean;
	isOffCenter: boolean;
	activeTestId: LIFE_TEST_ID | null;
	hasInsufficientTransitions: boolean;
	hasInvalidHeadMovements: boolean;
	hasInsufficientEmotion: boolean;
	complete: boolean;
	speech: string | null;
	speechStateId: string | null;
}

export interface IPolTestStatus {
	id: string;
	repeatTime: number;
	promptConfig: IPolPromptConfig[];
	conditions: () => boolean;
}

export interface IPolPromptConfig {
	audio: string;
	centerText: string;
	alert: IAlert;
	keepPrevAlert?: boolean; // Si es true, conserva la alerta en pantalla en lugar de sobreescribirla
}

// Segundos de los siguientes controles
export const POL_WARNING_TIME: number = 90;
export const POL_NO_FACE_TIME: number = 20;
export const POL_FINISH_TIME: number = 1;
export const POL_MIDDLE_FACE_AWAY: number = 4;
export const POL_BACK_TO_OCR_TIME: number = 7;

/** sourceIds habituales para frames live en ko-debug PoL (NIF-383). */
export const POL_LIVE_FRAME_SOURCE_IDS = {
	DEFAULT: 'ko-live-frame',
	FINAL: 'ko-final-frame',
	ATTEMPT: 'ko-attempt-frame',
	MULTIPLE_FACES: 'multiple-faces-frame',
} as const;

/** Detección múltiples caras: bbox mínima por rostro (~1,5 % canvas). */
export const POL_MULTIPLE_FACE_MIN_BBOX_AREA_RATIO = 0.015;
/** Detección múltiples caras: separación mínima entre centros (~12 % dimensión menor). */
export const POL_MULTIPLE_FACE_MIN_CENTER_SEPARATION_RATIO = 0.12;
/** Margen inicial antes de evaluar múltiples caras (segundos desde `general.startAt`). */
export const POL_MULTIPLE_FACES_START_COOLDOWN_SEC = 3;

/** Gracia tras switch de cámara antes de KO técnico / recovery agresivo (`life-check`). */
export const POL_CAMERA_CHANGE_GRACE_MS = 5000;
/** Poll lento de `predictWebcam` cuando el preview no tiene dimensiones útiles. */
export const POL_DEGRADED_POLL_MS = 400;
/**
 * Racha consecutiva (poll watchdog ~1,5 s) con preview muerto (video sin dims + `tec-scan-face` 0×0)
 * antes de escalar al flujo sin permisos (`onPermissionsDenied`).
 */
export const POL_OVAL_PREVIEW_DEAD_STREAK_THRESHOLD = 4;
/**
 * Tiempo mínimo tras `middleFace.startAt` antes de evaluar análisis muerto con preview vivo
 * (evita falsos positivos en el arranque de MediaPipe).
 */
export const POL_ANALYSIS_DEAD_MIN_ELAPSED_MS = 12_000;
/** Reintentos de recovery de frame inválido antes de escalar estrategia. */
export const POL_FRAME_RECOVERY_RETRY_COUNT = 4;
export const POL_FRAME_RECOVERY_RETRY_DELAY_MS = 180;
/** Fallos de recovery consecutivos antes de `ko-previewUnusable`. */
export const POL_FRAME_RECOVERY_MAX_FAILURES_BEFORE_TECH_KO = 2;
/** Espera tras `changeCamera` en recovery automático POL. */
export const POL_RECOVERY_CHANGE_CAMERA_SETTLE_MS = 450;
/** Heartbeat del buffer de telemetría con preview degradado. */
export const POL_PREVIEW_DIAGNOSTICS_HEARTBEAT_INTERVAL_MS = 30000;

/** Intervalo mínimo entre inferencias MediaPipe (~30 FPS). */
export const POL_DETECT_MIN_INTERVAL_MS = 33;
/** Conversión de `minTime` (segundos) a ms en timers POL. */
export const POL_MIN_TIME_MS_PER_SECOND = 1000;
/** Regenerar subtítulo/alerta como máximo cada N frames de rAF. */
export const POL_UI_REFRESH_EVERY_N_FRAMES = 4;

/** Cada cuántos frames de `predictWebcam` se ejecuta `lifeTestCycle` (mín/máx bajo estrés). */
export const POL_LIFE_TEST_CYCLE_MIN_FRAMES = 2;
export const POL_LIFE_TEST_CYCLE_MAX_FRAMES = 4;
export const POL_SLOW_FRAME_THRESHOLD_MS = 50;
export const POL_FAST_FRAME_THRESHOLD_MS = 35;
export const POL_SLOW_STREAK_TO_INCREASE_N = 4;
export const POL_FAST_STREAK_TO_DECREASE_N = 4;
/** Fallback de feedback entre pruebas si no hay `timeBetweenTest` válido. */
export const POL_SUCCESS_FEEDBACK_FALLBACK_MS = 700;

/** Señal “sin cara” en rAF: frames consecutivos y gracia tras última detección. */
export const POL_NO_FACE_MIN_CONSECUTIVE_FRAMES = 20;
export const POL_NO_FACE_RECENT_FACE_GRACE_MS = 2500;

/** Histéresis del óvalo `matchUi` (frames consecutivos). */
export const POL_MATCH_UI_STREAK_TO_CENTERED = 3;
export const POL_MATCH_UI_STREAK_TO_OFF_CENTER = 5;

/** Preview PoL: factor de zoom según dispositivo (UA). */
export type PolPreviewDevice = 'pc' | 'mobile';

export interface IPolConf {
	middleFace: IMiddleFace;
	lifeTest: Array<ILifeTest>;
	general: ILifeTestGeneralConf;
}

export interface ILifeTestGeneralConf {
	startAt: string;
	finishAt: string;
	type: POL_TYPE;
	totalLifeTest: number;
	timeBetweenTest: number; // seg
	messages: IMessages;
	audios?: IAudios;
	tries: number;
	countTries: number;
	status: TestStatus;
	observations: Array<string>;
	complete: boolean;
	controlValidation: boolean;
	minPercenteageTimeInMiddle: number;
	minPercenteageBlink: number;
	minEnvironmentSimilarityPercenteage: number;
	minFaceSimilarityPercenteage: number;
	minTotalSimilarityPercenteage: number;
	minScoring: number;
	scoring?: ILifeTestGeneralScoring;
	operationId: string;
}
export interface IMiddleFace {
	lifeTestId: LIFE_TEST_ID.MIDDLEFACE;
	complete: boolean;
	status: TestStatus;
	messages: IMessages;
	audios?: IAudios;
	minTime: number;
	maxTime: number;
	startAt: string;
	finishAt: string;
	photos: Array<{ img: string; label: string }>;
	scoring?: ILifeTestMiddleFaceScoring;
	fullScoring?: ILifeTestMiddleFaceScoring[];
}

export interface ILifeTest {
	lifeTestId: LIFE_TEST_ID;
	photos: Array<{ img: string; label: string }>;
	status: TestStatus;
	complete: boolean;
	startAt: string;
	finishAt: string;
	minTime: number;
	maxTime: number;
	messages: IMessages;
	audios?: IAudios;
	minPercentageFaceBlendshape: number; // Porcentaje mínimo para dar la prueba como válida
	minTransitionsFaceBlendshape: number; // Entre 1 y 10: Número de intervalos mímimos para una transición correcta
	noseMovement?: INoseMovement;
	matchWarning?: boolean;
	scoring?: ILifeTestScoring | ILifeTestApproachFaceScoring;
	fullScoring?: Array<ILifeTestScoring | ILifeTestApproachFaceScoring>;
}

export interface IScoringRange {
	value: number;
	min: number;
	max: number;
}

export interface INoseMovement {
	minDistance: number; // Distancia entre el punto central de la nariz y el punto más alejado
	minPercentageVariance: number; // Porcentaje de puntos que deben estar dentro del rectangulo de control
	minTransitions: number; // Transiciones minimas desde el punto central al punto más alejado
}

export interface IEmotions {
	record: Array<number>; // Array con el % de emocion válida para la prueba
	result: Array<number>; // Array auxiliar
	total: Array<number>; // Array con el % de emoción desde que comenzó la prueba
}

export interface ISimilarity {
	environment: number;
	face: number;
}

export interface IPolPayload {
	/** Base64 por captura (orden fijo: cara frontal, luego cada sub-prueba PoV). */
	images?: Array<string>;
	/**
	 * Opcional, misma longitud que `images`: id de prueba PoV por foto (p. ej. `middle-face`, `blink`).
	 * El back la persiste y el customer con `data=images_selfie` devuelve una URL por prueba.
	 */
	images_pol_proof?: Array<string | null>;
	cameras: Array<string>;
	device_id: string;
	data: any;
	status: boolean;
}

export interface ILifeTestLog {
	method: string;
	data: any;
	info: TPolLogs | TOcrLogs | TDocsLogs | TProcessLogs | TConnectionLogs | THomeLogs | string;
	type: 'emergency' | 'alert' | 'critical' | 'error' | 'warning' | 'notice' | 'info' | 'debug';
	image?: string;
	op_uuid: string;
	deviceId?: string;
	deviceType?: 'primary' | 'secondary' | string;
}

export interface IScanFaceAnimations {
	grow: boolean;
	check: boolean;
	halfRight: boolean;
	halfLeft: boolean;
	halfTop: boolean;
	halfBottom: boolean;
	smile: boolean;
	showArrow: boolean;
	positionArrow: 'left' | 'right' | 'bottom' | 'top';
	progressValue: number;
}

export interface INoseMovementValues {
	[SCORING_PARAMETER.NoseDistance]: number;
	[SCORING_PARAMETER.NoseTransition]: number;
	[SCORING_PARAMETER.NoseVariance]: number;
}

export interface ILifeTestDataReturn {
	lifeTest: {
		lifeTestId: LIFE_TEST_ID;
		status: TestStatus;
		startAt: string;
		finishAt: string;
		photos: string[];
		scoring: ILifeTestMiddleFaceScoring | ILifeTestScoring | ILifeTestApproachFaceScoring;
		fullScoring: ILifeTestMiddleFaceScoring[] | Array<ILifeTestScoring | ILifeTestApproachFaceScoring>;
		conf: {
			maxTime: number;
			minTime: number;
			minPercentageFaceBlendshape?: number;
			minTransitionsFaceBlendshape?: number;
			noseMovement?: INoseMovement;
		};
	}[];
	general: {
		startAt: string;
		finishAt: string;
		status: TestStatus;
		observations: Array<string>;
		type: POL_TYPE;
		scoring: ILifeTestGeneralScoring;
		operationId: string;
		photos: string[];
		conf: {
			countTries: number;
			totalLifeTest: number;
			minEnvironmentSimilarityPercenteage: number;
			minFaceSimilarityPercenteage: number;
			minPercenteageBlink: number;
			minPercenteageTimeInMiddle: number;
			minScoring: number;
			minTotalSimilarityPercenteage: number;
		};
	};
}

export enum LIFE_TEST_ID {
	SMILE = 'smile',
	TURNRIGHTHEAD = 'turn-right-head',
	TURNLEFTHEAD = 'turn-left-head',
	LIFTHEAD = 'lift-head',
	LOWERHEAD = 'lower-head',
	APPROACHFACE = 'approach-face',
	MIDDLEFACE = 'middle-face',
	BLINK = 'blink',
	GENERAL = 'general',
}

/** Pruebas de vida de tipo giro de cabeza (comparten estados de audio). */
export const POL_TURN_LIFE_TEST_IDS: LIFE_TEST_ID[] = [
	LIFE_TEST_ID.TURNRIGHTHEAD,
	LIFE_TEST_ID.TURNLEFTHEAD,
	LIFE_TEST_ID.LIFTHEAD,
	LIFE_TEST_ID.LOWERHEAD,
];

export function isPolTurnLifeTest(testId: LIFE_TEST_ID): boolean {
	return POL_TURN_LIFE_TEST_IDS.includes(testId);
}

export enum POL_TYPE {
	RANDOM = 'random',
	RANDOM_REPEAT = 'random-repeat',
	LINEAL = 'lineal',
}

export enum ControlRect {
	Width = 140,
	Height = 180,
	ExtraWidth = 285,
	ExtraHeight = 500,
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
	ExternalWidth = 285,
	ExternalHeight = 450,
}

export enum NoseControlRect {
	MarginCenter = 45,
	Width = 90,
	Long = 210,
}

export enum FaceLandmark {
	Nose = 4,
	Forehead = 10,
	Chin = 152,
	LeftEar = 454,
	RightEar = 234,
}

// Scoring
export enum SCORING_PARAMETER {
	SimilarityPercentage = 'similarityPercentage',
	SimilarityPercentageSeparate = 'similarityPercentageSeparate',
	PercentageTimeInTheMiddle = 'percentageTimeInTheMiddle',
	TotalTime = 'totalTime',
	Emotion = 'emotion',
	MaxEmotion = 'maxEmotion',
	EmotionTransition = 'emotionTransition',
	NoseDistance = 'noseDistance',
	NoseTransition = 'noseTransition',
	NoseVariance = 'noseVariance',
	FinalScore = 'finalScore',
	ShapeScore = 'shapeScore',
	AppearanceScore = 'appearanceScore',
	DepthDelta = 'depthDelta',
	Result = 'result',
	TotalTries = 'totalTries',
	NumFaces = 'numFaces',
	BlinksByMin = 'blinksByMin',
	BlinksByMinV2 = 'blinksByMinV2',
	BlinksByMinV1 = 'blinksByMinV1',
	Photos = 'photos',
	LifeTests = 'lifeTests',
}
export interface ILifeTestGeneralScoring {
	[SCORING_PARAMETER.Emotion]: any;
	[SCORING_PARAMETER.BlinksByMin]: number;
	[SCORING_PARAMETER.SimilarityPercentage]: number;
	[SCORING_PARAMETER.PercentageTimeInTheMiddle]: number;
	[SCORING_PARAMETER.TotalTries]: number;
	[SCORING_PARAMETER.TotalTime]: number;
	[SCORING_PARAMETER.LifeTests]: number;
	[SCORING_PARAMETER.Result]: number;
}

export interface ILifeTestMiddleFaceScoring {
	[SCORING_PARAMETER.SimilarityPercentage]: number;
	[SCORING_PARAMETER.PercentageTimeInTheMiddle]: number;
	[SCORING_PARAMETER.TotalTime]: number;
	[SCORING_PARAMETER.TotalTries]: number;
	[SCORING_PARAMETER.Result]: number;
}

export interface ILifeTestScoring {
	[SCORING_PARAMETER.Emotion]: any;
	[SCORING_PARAMETER.MaxEmotion]: number;
	[SCORING_PARAMETER.EmotionTransition]: number;
	[SCORING_PARAMETER.SimilarityPercentage]: number;
	[SCORING_PARAMETER.PercentageTimeInTheMiddle]: number;
	[SCORING_PARAMETER.TotalTime]: number;
	[SCORING_PARAMETER.TotalTries]: number;
	[SCORING_PARAMETER.NoseDistance]: number;
	[SCORING_PARAMETER.NoseTransition]: number;
	[SCORING_PARAMETER.NoseVariance]: number;
	[SCORING_PARAMETER.Result]: number;
}

/** Scoring de subprueba approach-face (NIF-739); no usa Emotion/Nose. */
export interface ILifeTestApproachFaceScoring {
	[SCORING_PARAMETER.FinalScore]: number;
	[SCORING_PARAMETER.ShapeScore]: number;
	[SCORING_PARAMETER.AppearanceScore]: number;
	[SCORING_PARAMETER.DepthDelta]: number;
	[SCORING_PARAMETER.PercentageTimeInTheMiddle]: number;
	[SCORING_PARAMETER.TotalTime]: number;
	[SCORING_PARAMETER.TotalTries]: number;
	[SCORING_PARAMETER.Result]: number;
}

export interface IPoLScoring {
	[LIFE_TEST_ID.SMILE]: {
		[SCORING_PARAMETER.Emotion]: IScoringParam;
		[SCORING_PARAMETER.MaxEmotion]: IScoringParam;
		[SCORING_PARAMETER.EmotionTransition]: IScoringParam;
		[SCORING_PARAMETER.SimilarityPercentage]: IScoringParam;
		[SCORING_PARAMETER.PercentageTimeInTheMiddle]: IScoringParam;
		[SCORING_PARAMETER.TotalTime]: IScoringParam;
		[SCORING_PARAMETER.TotalTries]: IScoringParam;
	};
	[LIFE_TEST_ID.APPROACHFACE]: {
		[SCORING_PARAMETER.FinalScore]: IScoringParam;
		[SCORING_PARAMETER.ShapeScore]: IScoringParam;
		[SCORING_PARAMETER.AppearanceScore]: IScoringParam;
		[SCORING_PARAMETER.DepthDelta]: IScoringParam;
		[SCORING_PARAMETER.PercentageTimeInTheMiddle]: IScoringParam;
		[SCORING_PARAMETER.TotalTime]: IScoringParam;
		[SCORING_PARAMETER.TotalTries]: IScoringParam;
	};
	[LIFE_TEST_ID.TURNRIGHTHEAD]: {
		[SCORING_PARAMETER.Emotion]: IScoringParam;
		[SCORING_PARAMETER.MaxEmotion]: IScoringParam;
		[SCORING_PARAMETER.EmotionTransition]: IScoringParam;
		[SCORING_PARAMETER.SimilarityPercentage]: IScoringParam;
		[SCORING_PARAMETER.PercentageTimeInTheMiddle]: IScoringParam;
		[SCORING_PARAMETER.TotalTime]: IScoringParam;
		[SCORING_PARAMETER.TotalTries]: IScoringParam;
		[SCORING_PARAMETER.NoseDistance]: IScoringParam;
		[SCORING_PARAMETER.NoseTransition]: IScoringParam;
		[SCORING_PARAMETER.NoseVariance]: IScoringParam;
	};
	[LIFE_TEST_ID.TURNLEFTHEAD]: {
		[SCORING_PARAMETER.Emotion]: IScoringParam;
		[SCORING_PARAMETER.MaxEmotion]: IScoringParam;
		[SCORING_PARAMETER.EmotionTransition]: IScoringParam;
		[SCORING_PARAMETER.SimilarityPercentage]: IScoringParam;
		[SCORING_PARAMETER.PercentageTimeInTheMiddle]: IScoringParam;
		[SCORING_PARAMETER.TotalTime]: IScoringParam;
		[SCORING_PARAMETER.TotalTries]: IScoringParam;
		[SCORING_PARAMETER.NoseDistance]: IScoringParam;
		[SCORING_PARAMETER.NoseTransition]: IScoringParam;
		[SCORING_PARAMETER.NoseVariance]: IScoringParam;
	};
	[LIFE_TEST_ID.LIFTHEAD]: {
		[SCORING_PARAMETER.Emotion]: IScoringParam;
		[SCORING_PARAMETER.MaxEmotion]: IScoringParam;
		[SCORING_PARAMETER.EmotionTransition]: IScoringParam;
		[SCORING_PARAMETER.SimilarityPercentage]: IScoringParam;
		[SCORING_PARAMETER.PercentageTimeInTheMiddle]: IScoringParam;
		[SCORING_PARAMETER.TotalTime]: IScoringParam;
		[SCORING_PARAMETER.TotalTries]: IScoringParam;
		[SCORING_PARAMETER.NoseDistance]: IScoringParam;
		[SCORING_PARAMETER.NoseTransition]: IScoringParam;
		[SCORING_PARAMETER.NoseVariance]: IScoringParam;
	};
	[LIFE_TEST_ID.LOWERHEAD]: {
		[SCORING_PARAMETER.Emotion]: IScoringParam;
		[SCORING_PARAMETER.MaxEmotion]: IScoringParam;
		[SCORING_PARAMETER.EmotionTransition]: IScoringParam;
		[SCORING_PARAMETER.SimilarityPercentage]: IScoringParam;
		[SCORING_PARAMETER.PercentageTimeInTheMiddle]: IScoringParam;
		[SCORING_PARAMETER.TotalTime]: IScoringParam;
		[SCORING_PARAMETER.TotalTries]: IScoringParam;
		[SCORING_PARAMETER.NoseDistance]: IScoringParam;
		[SCORING_PARAMETER.NoseTransition]: IScoringParam;
		[SCORING_PARAMETER.NoseVariance]: IScoringParam;
	};
	[LIFE_TEST_ID.MIDDLEFACE]: {
		[SCORING_PARAMETER.SimilarityPercentage]: IScoringParam;
		[SCORING_PARAMETER.PercentageTimeInTheMiddle]: IScoringParam;
		[SCORING_PARAMETER.TotalTime]: IScoringParam;
		[SCORING_PARAMETER.TotalTries]: IScoringParam;
	};
	[LIFE_TEST_ID.GENERAL]: {
		[SCORING_PARAMETER.Emotion]: IScoringParam;
		[SCORING_PARAMETER.BlinksByMin]: IScoringParam;
		[SCORING_PARAMETER.SimilarityPercentage]: IScoringParam;
		[SCORING_PARAMETER.PercentageTimeInTheMiddle]: IScoringParam;
		[SCORING_PARAMETER.TotalTries]: IScoringParam;
		[SCORING_PARAMETER.TotalTime]: IScoringParam;
		[SCORING_PARAMETER.LifeTests]: IScoringParam;
	};
}

export interface IScoringParam {
	type: SCORING_TYPE;
	ranges: IScoringRange[];
	weight: number;
}

export enum SCORING_TYPE {
	Percentage = 'Percentage',
	Time = 'time',
	Count = 'count',
	Pixel = 'px',
}

export enum IFRAME_RESULT {
	OK = 'ok',
	KO = 'ko',
	CANCEL = 'cancel',
}

export interface IPolResponse {
	code: number;
	result: 'OK' | 'KO';
	process?: IOperation[];
	retry: boolean;
}

export interface IPolErrorResponse {
	code: ERROR_STATUS;
	error: {
		error_code: ERROR_CODE;
		msg: string;
	};
	result: 'OK' | 'KO';
	retry: boolean;
}

export interface IPolManualConf {
	general: {
		startAt: string;
		finishAt: string;
		maxTime: number;
		status: TestStatus;
		photo?: { img: string; label: string };
		observations?: Array<string>;
	};
}
